import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { useAnimateExpand } from '@adflow/frontend/ui-common';
import { EdgeShadow } from '@adflow/frontend/ui-layout';
import { theme } from '@adflow/frontend/ui-theme';
import { scrollIntoView, useOnScreen } from '@adflow/frontend/util-common';

type OnCancel<T> = (
	data: T,
	expandedRow: boolean,
	toggleRow?: () => void
) => void;

type OnSave<T> = OnCancel<T>;

interface FrontMatterWithTransitionProps<T> {
	data: T;
	dataRowBelow?: T;
	toggleRowExpanded?: () => void;
	index?: number;
	children: (props: {
		data: T;
		index?: number;
		onCancel: () => void;
		onSave: () => void;
	}) => ReactElement;
	onCancel: OnCancel<T>;
	onSave: OnSave<T>;
	collapsedHeight?: number;
}

export function FrontMatterWithTransition<T>({
	data,
	dataRowBelow,
	toggleRowExpanded,
	index,
	onSave,
	onCancel,
	children,
	collapsedHeight = 56,
}: FrontMatterWithTransitionProps<T>) {
	const [intervalIdState, setIntervalIdState] = useState<
		NodeJS.Timeout | undefined
	>();

	const scrollFn = () => {
		const scrollRef = containerProps.ref.current as Element | null;

		if (scrollRef && scrollRef.ownerDocument) {
			// On slow machines the scroolIntoView can not work from the first time
			// so we need to call it multiple times until it works
			const intervalId = setInterval(() => {
				scrollIntoView(scrollRef, { block: 'center' });
				setIntervalIdState(intervalId);
			}, 500);
		}
	};

	const { collapse, expand, containerProps } = useAnimateExpand<HTMLDivElement>(
		{
			collapsedHeight,
			onExpandComplete: scrollFn,
		}
	);

	const isVisible = useOnScreen(containerProps.ref);

	useEffect(() => {
		// If the row is already visible and the interval is set, clear it
		if (isVisible && intervalIdState) {
			const intervalToClear = intervalIdState;
			setIntervalIdState(undefined);
			clearInterval(intervalToClear);
		}
	}, [isVisible, intervalIdState]);

	useEffect(() => {
		expand();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const delayedHandler = (delayedFunction: OnCancel<T> | OnSave<T>) => {
		requestAnimationFrame(() => {
			collapse();
		});

		setTimeout(() => {
			delayedFunction(data, true, undefined);
		}, 500);
	};

	if (toggleRowExpanded) {
		return children({
			data,
			index,
			onSave: () => setTimeout(() => onSave(data, true, toggleRowExpanded), 0),
			onCancel: () =>
				setTimeout(() => onCancel(data, true, toggleRowExpanded), 0),
		});
	}

	return (
		<div
			css={[
				{
					borderColor: theme.colors.input.border,
					backgroundColor: theme.colors.background.form,
					position: 'relative',
					borderTopWidth: '1px',
				},
				!dataRowBelow && { borderBottomWidth: '1px' },
			]}
		>
			<div {...containerProps}>
				{children({
					data,
					index,
					onSave: () => delayedHandler(onSave),
					onCancel: () => delayedHandler(onCancel),
				})}
			</div>
			{dataRowBelow ? null : <EdgeShadow side="bottom" />}
			<EdgeShadow side="top" />
		</div>
	);
}
